/**
 * Die beiden Adressen dieses Systems — aus dem Hostnamen abgeleitet.
 *
 * ============================================================================
 * WARUM SIE NICHT MEHR EINGEBACKEN WERDEN (20.09.2026)
 * ============================================================================
 *
 * Bis hierher kamen sie als Build-Argumente ins Abbild (`VITE_CRM_URL`,
 * `VITE_TRAINER_URL`, apps/web/Dockerfile). Das trug, solange es EIN Haus mit
 * EINEM Namen gab. Mit mehreren Mandanten kippt es: Ein Abbild je Kunde wäre
 * ein Build je Kunde — und damit genau die Vervielfachung von Ausrollen und
 * Aktualisieren, die das Betriebsmodell nicht trägt.
 *
 * Die Adressen folgen deshalb dem Namensschema und werden im Browser
 * gerechnet:
 *
 *     musterhausen.tritixcrm.de          → die Arbeitsoberfläche
 *     musterhausen-trainer.tritixcrm.de  → der Trainerbereich
 *
 * Der Zusatz steht am ERSTEN Namensteil und nicht als weitere Ebene
 * (`trainer.musterhausen.…`), weil ein Wildcard-Zertifikat genau eine Ebene
 * abdeckt. `*.tritixcrm.de` passt auf beide Namen oben; auf eine dritte Ebene
 * passt es nicht.
 *
 * DIE EINGEBACKENEN WERTE BLEIBEN ALS ÜBERSCHREIBUNG. Eine kundeneigene
 * Domain folgt dem Schema nicht; dort setzt die Umgebung die Adresse
 * ausdrücklich. Und in der Entwicklung (`pnpm dev`, Prüflauf) gibt es keinen
 * passenden Hostnamen — dann gilt der Pfad-Rückfall.
 *
 * DER RÜCKFALL IST EIN PFAD UND KEIN NAME: Ein Pfad führt höchstens an die
 * falsche Stelle derselben Anwendung, ein falscher Name ins Nichts.
 */

/** Der Zusatz, der den Trainerbereich vom CRM trennt. */
const TRAINERZUSATZ = '-trainer';

/**
 * Rechnet aus dem laufenden Hostnamen die Adresse der jeweils anderen
 * Oberfläche — oder gibt `null` zurück, wenn das Schema nicht greift.
 */
export function ausHostnamen(
  hostname: string,
  ziel: 'crm' | 'trainer',
  schema: 'https' | 'http' = 'https',
): string | null {
  /* Ohne Punkt ist es `localhost` oder ein Behälternamen — kein Schema. */
  if (!hostname.includes('.')) return null;

  const [erster, ...rest] = hostname.split('.');
  if (erster === undefined || rest.length === 0) return null;

  if (ziel === 'trainer') {
    /* Schon im Trainerbereich: derselbe Name. */
    const name = erster.endsWith(TRAINERZUSATZ) ? erster : `${erster}${TRAINERZUSATZ}`;
    return `${schema}://${[name, ...rest].join('.')}`;
  }

  if (!erster.endsWith(TRAINERZUSATZ)) return `${schema}://${hostname}`;
  return `${schema}://${[erster.slice(0, -TRAINERZUSATZ.length), ...rest].join('.')}`;
}

function adresse(ziel: 'crm' | 'trainer', rueckfall: string): string {
  const gesetzt = ziel === 'crm' ? import.meta.env.VITE_CRM_URL : import.meta.env.VITE_TRAINER_URL;
  if (gesetzt !== undefined && gesetzt !== '') return gesetzt;

  if (typeof window === 'undefined') return rueckfall;
  const schema = window.location.protocol === 'http:' ? 'http' : 'https';
  return ausHostnamen(window.location.hostname, ziel, schema) ?? rueckfall;
}

/** Wo der Trainerbereich liegt (RES-27). */
export const TRAINERADRESSE = adresse('trainer', '/trainer.html');

/** Wo die Arbeitsoberfläche des Hauses liegt. */
export const CRMADRESSE = adresse('crm', '/');
